import React, { useEffect, useState } from 'react';
import { Spin, Tabs } from 'antd';
import { useIntl } from 'react-intl';
import { ObtPartnerLegal, ObtPeppolData, ObtUserRestriction } from '../../../__generated__/types';
import { useGetObtPeppolDataLazyQuery } from '../obtQueries.generated';
import { QUERY_USER_RESTRICTION } from '../obtQueries';
import client from '../../../config/GraphQLApolloClient';
import ObtItalyLegalForm from './ObtItalyLegalForm';
import ObtGermanyLeitwegsForm from './ObtGermanyLeitwegsForm';
import ObtPeppolForm from './ObtPeppolForm';
import ObtPolandVatForm from './ObtPolandVatForm';

// ─── Types ─────────────────────────────────────────────────────────────────────

type ObtLegalTabProps = {
    partnerLegalData: ObtPartnerLegal[];
    partnerno: string;
    companycode: string;
    idpartner: number;
    country: string;
    canUpdate: boolean;
};

// ─── Component ─────────────────────────────────────────────────────────────────

const ObtLegalTab: React.FC<ObtLegalTabProps> = ({
    partnerLegalData,
    partnerno,
    companycode,
    idpartner,
    country,
    canUpdate,
}) => {
    const intl = useIntl();
    const [peppolData, setPeppolData] = useState<ObtPeppolData[] | undefined>(undefined);
    const [userRestrictions, setUserRestrictions] = useState<ObtUserRestriction[]>([]);
    const [loading, setLoading] = useState(false);

    const [getPeppolData] = useGetObtPeppolDataLazyQuery({ fetchPolicy: 'network-only' });

    // Fetch PEPPOL data by country on mount
    useEffect(() => {
        if (country) {
            setLoading(true);
            getPeppolData({ variables: { country } })
                .then((response) => {
                    setPeppolData((response.data?.obtGetObtPeppolData as ObtPeppolData[]) ?? undefined);
                })
                .catch((err) => console.error('Failed to load PEPPOL data:', err))
                .finally(() => setLoading(false));
        }
    }, [country, getPeppolData]);

    // Fetch user restrictions on mount
    useEffect(() => {
        client
            .query({ query: QUERY_USER_RESTRICTION, fetchPolicy: 'network-only' })
            .then((response) => {
                setUserRestrictions(response.data?.obtGetUserRestriction ?? []);
            })
            .catch((err) => console.error('Failed to load user restrictions:', err));
    }, []);

    const legalRecord = partnerLegalData?.[0] ?? ({} as ObtPartnerLegal);
    const isPoland = country?.toLowerCase() === 'pl';

    const tabItems = [
        {
            key: 'legalItaly',
            label: intl.formatMessage({ id: 'obt.legal.tab.italy', defaultMessage: 'Italy' }),
            children: (
                <ObtItalyLegalForm
                    partnerLegalData={legalRecord}
                    partnerno={partnerno}
                    companycode={companycode}
                    idpartner={idpartner}
                    canUpdate={canUpdate}
                    userRestrictions={userRestrictions}
                />
            ),
        },
        {
            key: 'legalGermany',
            label: intl.formatMessage({ id: 'obt.legal.tab.germany', defaultMessage: 'Germany' }),
            children: (
                <ObtGermanyLeitwegsForm
                    partnerLegalData={legalRecord}
                    partnerno={partnerno}
                    companycode={companycode}
                    idpartner={idpartner}
                    canUpdate={canUpdate}
                    userRestrictions={userRestrictions}
                />
            ),
        },
        {
            key: 'legalPeppol',
            label: intl.formatMessage({ id: 'obt.legal.tab.peppol', defaultMessage: 'PEPPOL' }),
            children: (
                <ObtPeppolForm
                    currentPeppolId={legalRecord.peppolId}
                    partnerno={partnerno}
                    companycode={companycode}
                    idpartner={idpartner}
                    country={country}
                    peppolData={peppolData}
                    canUpdate={canUpdate}
                    userRestrictions={userRestrictions}
                />
            ),
        },
        ...(isPoland
            ? [
                  {
                      key: 'legalPoland',
                      label: intl.formatMessage({ id: 'obt.legal.tab.poland', defaultMessage: 'Poland VAT (NIP)' }),
                      children: (
                          <ObtPolandVatForm
                              partnerno={partnerno}
                              idpartner={idpartner}
                              canUpdate={canUpdate}
                          />
                      ),
                  },
              ]
            : []),
    ];

    return (
        <Spin spinning={loading} size="large">
            <Tabs
                type="card"
                size="small"
                style={{ marginTop: 10 }}
                items={tabItems}
            />
        </Spin>
    );
};

export default ObtLegalTab;

